import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  PropsWithChildren,
  useCallback,
} from "react";
import { useAudioVideo } from "amazon-chime-sdk-component-library-react";
import TranscriptEvent from "amazon-chime-sdk-js/build/transcript/TranscriptEvent";
import Transcript from "amazon-chime-sdk-js/build/transcript/Transcript";
import { extractRoomMemberData } from "../utils/roomMemberDataUtils";
import { useVideo, useVideoDispatch, SET_CC_STATUS } from "./VideoContext";
import { 
  turnOnMeetingTranscription
} from "../utils/api/transcription";

// Types for transcription data based on AWS Chime SDK
export interface TranscriptionSegment {
  id: string;
  text: string;
  speakerId: string;
  speakerName: string;
  startTime: number;
  endTime: number;
  isPartial: boolean;
  confidence: number;
}

export interface TranscriptionState {
  isTranscriptionDisabled: boolean;
  isTranscribing: boolean;
  segments: TranscriptionSegment[];
  currentSegment: TranscriptionSegment | null;
  lines: string[]; // For displaying transcript lines
  isLoading: boolean; // Loading state when starting transcription
  error: string | null; // Error state for transcription operations
}

interface TranscriptionContextType {
  state: TranscriptionState;
  toggleTranscription: () => void;
  isTranscriptionAvailable: boolean; // Whether transcription can be toggled
}

const TranscriptionContext = createContext<
  TranscriptionContextType | undefined
>(undefined);

const initialState: TranscriptionState = {
  isTranscriptionDisabled: false,
  isTranscribing: false,
  segments: [],
  currentSegment: null,
  lines: [],
  isLoading: false,
  error: null,
};

export const TranscriptionProvider: React.FC<
  PropsWithChildren<{
    isTranscriptionDisabled: boolean;
  }>
> = ({ children, isTranscriptionDisabled }) => {
  const audioVideo = useAudioVideo();
  const { meetingDetails, ccStatus } = useVideo();
  const dispatch = useVideoDispatch();
  const [state, setState] = useState<TranscriptionState>(initialState);

  const lastPartialText = useRef<string | null>(null);
  const lastPartialUpdate = useRef<number>(0);

  // Check if transcription is available (meeting exists and user has access)
  const isTranscriptionAvailable = Boolean(meetingDetails?.id);

  // Update transcription state based on ccStatus from heartbeat
  // When ccStatus becomes 'off', automatically turn off the local toggle
  useEffect(() => {
    if (ccStatus === 'off' && state.isTranscribing) {
      // Transcription was turned off externally (by background job or another user)
      // Turn off the local toggle to hide subtitles
      setState(prev => ({
        ...prev,
        isTranscribing: false,
        error: null,
      }));
    }
  }, [ccStatus, state.isTranscribing]);

  // Subscribe to transcription events using the Chime SDK
  useEffect(() => {
    // If transcription is not enabled or not transcribing, don't subscribe to transcription events
    if (isTranscriptionDisabled || !state.isTranscribing) {
      return;
    }

    if (!audioVideo) {
      return;
    }

    // Check if transcription controller is available
    if (audioVideo.transcriptionController) {
      const transcriptCallback = (transcriptEvent: TranscriptEvent) => {
        if (!(transcriptEvent instanceof Transcript)) return;
        if (!transcriptEvent.results || transcriptEvent.results.length === 0) return;

        const result = transcriptEvent.results[0];
        const alternative = result.alternatives?.[0];
        if (!alternative) return;
        const text = alternative.transcript.trim();
        const attendee = alternative.items?.[0]?.attendee || null;
        const speakerId = attendee?.externalUserId || "unknown";
        const { displayName: speakerName } = attendee
          ? extractRoomMemberData(attendee)
          : { displayName: "Speaker" };

        const timestamp = Date.now();
        if (!result.isPartial) {
          const confidence =
            alternative.items && alternative.items.length > 0
              ? alternative.items[0].confidence || 0
              : 0;

          if (confidence > 0.5 && text.length > 0) {
            const newSegment: TranscriptionSegment = {
              id: timestamp.toString(),
              text,
              speakerId,
              speakerName,
              startTime: timestamp,
              endTime: timestamp,
              isPartial: false,
              confidence,
            };

            const line = `${speakerId}: ${text}`;

            setState((prev) => ({
              ...prev,
              currentSegment: newSegment,
              segments: [...prev.segments, newSegment],
              lines: [...prev.lines, line],
            }));

            // Reset partial tracking
            lastPartialText.current = null;
            lastPartialUpdate.current = 0;
          }
        } else {
          // Skip short/empty partials
          if (text.length < 6) return;

          const didChange = text !== lastPartialText.current;
          const now = Date.now();
          const timeSinceLast = now - lastPartialUpdate.current;

          if (didChange && timeSinceLast > 300) {
            lastPartialText.current = text;
            lastPartialUpdate.current = now;

            const partialSegment: TranscriptionSegment = {
              id: `partial-${now}`,
              text,
              speakerId,
              speakerName,
              startTime: now,
              endTime: now,
              isPartial: true,
              confidence: 0,
            };

            setState((prev) => ({
              ...prev,
              currentSegment: partialSegment,
            }));
          }
        }
      };

      // Subscribe to transcript events
      audioVideo.transcriptionController.subscribeToTranscriptEvent(
        transcriptCallback
      );

      return () => {
        // Unsubscribe from transcript events
        if (audioVideo.transcriptionController) {
          audioVideo.transcriptionController.unsubscribeFromTranscriptEvent(
            transcriptCallback
          );
        }
      };
    }
  }, [audioVideo, isTranscriptionDisabled, state.isTranscribing]);

  // Toggle transcription function
  const toggleTranscription = useCallback(async () => {
    if (!isTranscriptionAvailable || !meetingDetails?.id) {
      setState(prev => ({
        ...prev,
        error: 'Transcription not available for this meeting',
      }));
      return;
    }

    // If transcription is currently on, just toggle local state (turn off subtitles)
    if (state.isTranscribing) {
      setState(prev => ({
        ...prev,
        isTranscribing: false,
        error: null,
      }));
    } else {
      // If transcription is off, check if it's already on via ccStatus
      if (ccStatus === 'on') {
        // Already on, just toggle local state to show subtitles
        setState(prev => ({
          ...prev,
          isTranscribing: true,
          error: null,
        }));
      } else {
        // Turn on transcription via API
        try {
          setState(prev => ({ ...prev, isLoading: true, error: null }));
          const response = await turnOnMeetingTranscription(meetingDetails.id);
          
          // If API call was successful, immediately update local state
          if (response.cc_status === 'on') {
            setState(prev => ({
              ...prev,
              isTranscribing: true,
              isLoading: false,
              error: null,
            }));
            
            // Also update the global ccStatus in VideoContext to keep it in sync
            dispatch({ type: SET_CC_STATUS, payload: { ccStatus: 'on' } });
          } else {
            setState(prev => ({
              ...prev,
              isLoading: false,
              error: 'Transcription started but status is not on',
            }));
          }
        } catch (error) {
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: 'Failed to turn on transcription',
          }));
        }
      }
    }
  }, [isTranscriptionAvailable, meetingDetails?.id, state.isTranscribing, ccStatus, dispatch]);

  const contextValue: TranscriptionContextType = {
    state: {
      ...state,
      isTranscriptionDisabled,
    },
    toggleTranscription,
    isTranscriptionAvailable,
  };

  return (
    <TranscriptionContext.Provider value={contextValue}>
      {children}
    </TranscriptionContext.Provider>
  );
};

export const useTranscription = (): TranscriptionContextType => {
  const context = useContext(TranscriptionContext);
  if (!context) {
    throw new Error(
      "useTranscription must be used within TranscriptionProvider"
    );
  }
  return context;
};